import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Spinner, Form, Row, Col, Button, Table } from "react-bootstrap";
import { DarkModeContext } from "../../context/DarkModeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faDownload, faPlus, faSave } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import logo from "../../assets/img/logo.png";
import { getToken } from "../../utils/auth";


/* ==========================
   Tipos válidos del SELECT (MAYÚSCULAS)
   ========================== */
const TIPOS_DOCUMENTO = [
    "DNI FRENTE",
    "DNI DORSO",
    "DOCUMENTACIÓN SOCIETARIA (ESTATUTO, PODERES)",
    "CONSTANCIA ARCA/AFIP",
    "CONSTANCIA RENTAS",
    "CONSTANCIA CBU A NOMBRE DEL TITULAR / RAZÓN SOCIAL",
    "FICHA ABM",
    "OTROS",
];

// Provincias estáticas
const PROVINCIAS = [
    "BUENOS AIRES", "CATAMARCA", "CHACO", "CHUBUT", "CÓRDOBA", "CORRIENTES",
    "ENTRE RÍOS", "FORMOSA", "JUJUY", "LA PAMPA", "LA RIOJA", "MENDOZA",
    "MISIONES", "NEUQUÉN", "RÍO NEGRO", "SALTA", "SAN JUAN", "SAN LUIS",
    "SANTA CRUZ", "SANTA FE", "SANTIAGO DEL ESTERO", "TIERRA DEL FUEGO", "TUCUMÁN"
];

// Helpers
const toBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result?.toString() || "");
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file);
    });

const cleanDataUrl = (dataUrl) => {
    if (!dataUrl) return "";
    const idx = dataUrl.indexOf(",");
    return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
};

const formatBytes = (bytes = 0) => {
    const b = Number(bytes || 0);
    if (b === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(b) / Math.log(k));
    return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

// mime -> extensión (fallback cuando no hay extensión en nombre)
const mimeToExt = (mime) => {
    const m = (mime || "").toLowerCase();
    if (m === "application/pdf") return "pdf";
    if (m === "image/jpeg" || m === "image/jpg") return "jpg";
    if (m === "image/png") return "png";
    if (m === "image/webp") return "webp";
    if (m === "text/plain") return "txt";
    if (m === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") return "docx";
    if (m === "application/msword") return "doc";
    if (m === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") return "xlsx";
    if (m === "application/vnd.ms-excel") return "xls";
    if (m === "application/zip") return "zip";
    return "";
};

// Deducir extensión real del archivo
const getFileExt = (file) => {
    if (!file) return null;
    // 1) por nombre
    const name = file.name || "";
    const dot = name.lastIndexOf(".");
    if (dot > -1 && dot < name.length - 1) {
        return name.slice(dot + 1).trim().toLowerCase();
    }
    // 2) por mime
    const byMime = mimeToExt(file.type);
    return byMime || null;
};

// Sanitizar nombre de archivo (permitimos espacios)
const sanitizeFilename = (s) =>
    (s || "")
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, "") // inválidos en Windows
        .replace(/\s+/g, " ")
        .trim();

// Descarga usando extensión real
const downloadBase64 = (base64, filenameBase, ext) => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);

    const lowerExt = (ext || "").toLowerCase();
    const mime =
        lowerExt === "pdf" ? "application/pdf" :
            lowerExt === "jpg" || lowerExt === "jpeg" ? "image/jpeg" :
                lowerExt === "png" ? "image/png" :
                    lowerExt === "webp" ? "image/webp" :
                        lowerExt === "txt" ? "text/plain" :
                            lowerExt === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" :
                                lowerExt === "doc" ? "application/msword" :
                                    lowerExt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" :
                                        lowerExt === "xls" ? "application/vnd.ms-excel" :
                                            lowerExt === "zip" ? "application/zip" :
                                                "application/octet-stream";

    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mime });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    const safeName = sanitizeFilename(filenameBase) || "DOCUMENTO";
    const finalExt = lowerExt ? `.${lowerExt}` : "";
    a.href = url;
    a.download = `${safeName}${finalExt}`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const safeJson = async (resp) => {
    try { return await resp.json(); } catch { return null; }
};

/* Normalizador de respuesta backend */
const normalizeDoc = (d) => ({
    IdDocumento: d?.IdDocumento ?? d?.idDocumento ?? d?.ID ?? d?.id,
    IdLegajo: d?.IdLegajo ?? d?.idLegajo,
    NombreDocumento: d?.NombreDocumento ?? d?.nombreDocumento,       // etiqueta del select
    DocumentoTipo: d?.DocumentoTipo ?? d?.documentoTipo ?? null,     // extensión real
    Bytes: d?.Bytes ?? d?.bytes ?? 0,
    Base64: d?.Base64 ?? d?.base64 ?? null,
});

/* ============ Componente ============ */
const ModalEditarLegajo = ({ show, onHide, legajo }) => {
    const { darkMode } = useContext(DarkModeContext);
    const token = useMemo(() => getToken() || "", []);
    const legajoId = legajo?.id ?? legajo?.Id ?? legajo?.ID;

    // ---------- Estado edición legajo ----------
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        NroLegajo: legajo?.nroLegajo ?? legajo?.NroLegajo ?? "",
        Terminal: legajo?.terminal ?? legajo?.Terminal ?? "",
        NombreDeFantasia: legajo?.nombreDeFantasia ?? legajo?.NombreDeFantasia ?? "",
        Provincia: legajo?.provincia ?? legajo?.Provincia ?? "",
        TipoPersona: legajo?.tipoPersona ?? legajo?.TipoPersona ?? "",
        CuitTitularOSociedad: legajo?.cuitTitularOSociedad ?? legajo?.CuitTitularOSociedad ?? "",
        RazonSocial: legajo?.razonSocial ?? legajo?.RazonSocial ?? "",
        Calle: legajo?.calle ?? legajo?.Calle ?? "",
        Numero: legajo?.numero ?? legajo?.Numero ?? "",
        PisoDepto: legajo?.pisoDepto ?? legajo?.PisoDepto ?? "",
        Localidad: legajo?.localidad ?? legajo?.Localidad ?? "",
        CodigoPostal: legajo?.codigoPostal ?? legajo?.CodigoPostal ?? "",
        EmailContacto: legajo?.emailContacto ?? legajo?.EmailContacto ?? "",
        CelularWhatsapp: legajo?.celularWhatsapp ?? legajo?.CelularWhatsapp ?? "",
        Asesor: legajo?.asesor ?? legajo?.Asesor ?? "",
    });

    useEffect(() => {
        if (show) {
            setForm({
                NroLegajo: legajo?.nroLegajo ?? legajo?.NroLegajo ?? "",
                Terminal: legajo?.terminal ?? legajo?.Terminal ?? "",
                NombreDeFantasia: legajo?.nombreDeFantasia ?? legajo?.NombreDeFantasia ?? "",
                Provincia: legajo?.provincia ?? legajo?.Provincia ?? "",
                TipoPersona: legajo?.tipoPersona ?? legajo?.TipoPersona ?? "",
                CuitTitularOSociedad: legajo?.cuitTitularOSociedad ?? legajo?.CuitTitularOSociedad ?? "",
                RazonSocial: legajo?.razonSocial ?? legajo?.RazonSocial ?? "",
                Calle: legajo?.calle ?? legajo?.Calle ?? "",
                Numero: legajo?.numero ?? legajo?.Numero ?? "",
                PisoDepto: legajo?.pisoDepto ?? legajo?.PisoDepto ?? "",
                Localidad: legajo?.localidad ?? legajo?.Localidad ?? "",
                CodigoPostal: legajo?.codigoPostal ?? legajo?.CodigoPostal ?? "",
                EmailContacto: legajo?.emailContacto ?? legajo?.EmailContacto ?? "",
                CelularWhatsapp: legajo?.celularWhatsapp ?? legajo?.CelularWhatsapp ?? "",
                Asesor: legajo?.asesor ?? legajo?.Asesor ?? "",
            });
        }
    }, [show, legajo]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const submitEditar = async (e) => {
        e.preventDefault();
        if (!token) {
            Swal.fire({ title: "Sesión", text: "Token no disponible.", imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo", confirmButtonText: "Ok" });
            return;
        }
        if (!legajoId) {
            Swal.fire({ title: "Legajo", text: "No se identificó el legajo.", imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo", confirmButtonText: "Ok" });
            return;
        }

        try {
            setSaving(true);
            const body = {
                token,
                Id: legajoId,
                NroLegajo: form.NroLegajo === "" ? undefined : Number(form.NroLegajo),
                Terminal: form.Terminal === "" ? undefined : form.Terminal,
                NombreDeFantasia: form.NombreDeFantasia || undefined,
                Provincia: form.Provincia || undefined,
                TipoPersona: form.TipoPersona || undefined,
                CuitTitularOSociedad: form.CuitTitularOSociedad || undefined,
                RazonSocial: form.RazonSocial || undefined,
                Calle: form.Calle || undefined,
                Numero: form.Numero || undefined,
                PisoDepto: form.PisoDepto || undefined,
                Localidad: form.Localidad || undefined,
                CodigoPostal: form.CodigoPostal || undefined,
                EmailContacto: form.EmailContacto || undefined,
                CelularWhatsapp: form.CelularWhatsapp || undefined,
                Asesor: form.Asesor || undefined,
            };

            const resp = await fetch(process.env.REACT_APP_API_LEGAJO_EDITAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!resp.ok) {
                const msg = await safeJson(resp);
                throw new Error(msg?.message || "No se pudo guardar los cambios");
            }

            Swal.fire({
                title: "Guardado",
                text: "Los cambios del legajo se guardaron.",
                imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo",
                confirmButtonText: "Ok", timer: 2500,
            }).then(() => onHide && onHide());
        } catch (err) {
            console.error(err);
            Swal.fire({ title: "Error", text: err.message || "No se pudo guardar", icon: "error" });
        } finally { setSaving(false); }
    };

    // ---------- Documentos ----------
    const [cargandoLista, setCargandoLista] = useState(false);
    const [docList, setDocList] = useState([]); // normalizados
    const [tipo, setTipo] = useState("");
    const [file, setFile] = useState(null);
    const [subiendo, setSubiendo] = useState(false);

    const cargarLista = async () => {
        setCargandoLista(true);
        try {
            const resp = await fetch(process.env.REACT_APP_API_DOCLEGAJO_METADATA, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token, IdLegajo: legajoId }),
            });
            if (!resp.ok) throw new Error("No se pudo obtener los documentos");
            const data = await resp.json();
            const arr = Array.isArray(data) ? data.map(normalizeDoc) : [];
            setDocList(arr);
        } catch (err) {
            console.error(err);
            setDocList([]);
        } finally { setCargandoLista(false); }
    };

    useEffect(() => {
        if (show && legajoId) cargarLista();
    }, [show, legajoId, token]);

    const refreshList = cargarLista;

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!token) {
            Swal.fire({ title: "Sesión", text: "Token no disponible.", imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo", confirmButtonText: "Ok" });
            return;
        }
        if (!legajoId) {
            Swal.fire({ title: "Legajo", text: "No se identificó el legajo.", imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo", confirmButtonText: "Ok" });
            return;
        }
        if (!tipo) {
            Swal.fire({ title: "Documento", text: "Seleccioná el TIPO de documento.", imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo", confirmButtonText: "Ok" });
            return;
        }
        if (!file) {
            Swal.fire({ title: "Documento", text: "Seleccioná un archivo.", imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo", confirmButtonText: "Ok" });
            return;
        }

        try {
            setSubiendo(true);
            const dataUrl = await toBase64(file);
            const base64 = cleanDataUrl(dataUrl);

            // Deducir extensión real del archivo para DocumentoTipo
            const ext = getFileExt(file); // ej: 'pdf','jpg','png','docx','zip', etc.

            const resp = await fetch(process.env.REACT_APP_API_DOCLEGAJO_CREAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdLegajo: legajoId,
                    NombreDocumento: tipo,       // etiqueta seleccionada (MAYÚSCULAS)
                    DocumentoTipo: ext || null,  // extensión real
                    ArchivoBase64: base64,       // contenido
                }),
            });

            if (!resp.ok) {
                const msg = await safeJson(resp);
                throw new Error(msg?.message || "No se pudo subir el documento");
            }

            await refreshList();
            setTipo("");
            setFile(null);

            Swal.fire({
                title: "Éxito",
                text: "Documento subido correctamente.",
                imageUrl: logo, imageWidth: 100, imageHeight: 30, imageAlt: "Logo",
                confirmButtonText: "Ok", timer: 2500,
            });
        } catch (err) {
            console.error(err);
            Swal.fire({ title: "Error", text: err.message || "No se pudo subir el documento", icon: "error" });
        } finally { setSubiendo(false); }
    };

    const handleDescargar = async (doc) => {
        try {
            const resp = await fetch(process.env.REACT_APP_API_DOCLEGAJO_DESCARGAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdLegajo: legajoId,
                    IdDocumento: doc.IdDocumento,
                }),
            });

            if (!resp.ok) {
                const msg = await safeJson(resp);
                throw new Error(msg?.message || "No se pudo descargar");
            }

            const data = await resp.json();
            const n = normalizeDoc(data);
            if (!n.Base64) throw new Error("No llegó el contenido del archivo.");

            // Nombre = NombreDocumento (del select), Extensión = DocumentoTipo real
            const baseName = sanitizeFilename(n.NombreDocumento || `DOCUMENTO_${n.IdDocumento}`);
            const ext = (n.DocumentoTipo || "").toLowerCase();
            downloadBase64(n.Base64, baseName, ext);
        } catch (err) {
            console.error(err);
            Swal.fire({
                title: "Error",
                text: err.message || "No se pudo descargar el documento",
                icon: "error",
            });
        }
    };

    const headerClass = darkMode ? "bg-white text-dark" : "bg-dark text-white";
    const textClass = darkMode ? "text-white" : "text-dark";

    return (
        <Modal show={show} onHide={onHide} centered size="lg" aria-labelledby="modal-editar-legajo">
            <Modal.Body className={darkMode ? "modal-content-2-dark" : "modal-content-2"}>
                {/* Header */}
                <section className="d-flex justify-content-between my-2 align-items-center">
                    <div />
                    <h6 className={`fs-18 lato-bold ${textClass} m-0`}>
                        Editar Legajo {legajo?.nroLegajo ?? legajo?.NroLegajo ?? ""}
                    </h6>
                    <button className="border-0 btn-filtro-cruz" onClick={onHide}>
                        <FontAwesomeIcon className={`fs-18 ${textClass}`} icon={faXmark} />
                    </button>
                </section>

                {/* Form edición */}
                <Form onSubmit={submitEditar} className="mt-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Label className={textClass}>N° de Legajo</Form.Label>
                            <Form.Control
                                name="NroLegajo"
                                type="number"
                                maxLength={10}
                                value={form.NroLegajo}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label className={textClass}>N° de Terminal</Form.Label>
                            <Form.Control
                                name="Terminal"
                                type="text"
                                maxLength={10}
                                value={form.Terminal}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={12}>
                            <Form.Label className={textClass}>Nombre de fantasía</Form.Label>
                            <Form.Control
                                name="NombreDeFantasia"
                                type="text"
                                value={form.NombreDeFantasia}
                                onChange={handleChange}
                                placeholder="Cordelia"
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Tipo de persona</Form.Label>
                            <Form.Select
                                name="TipoPersona"
                                value={form.TipoPersona || ""}
                                onChange={handleChange}
                                className={darkMode ? "bg-dark text-white" : ""}
                            >
                                <option value="">Seleccionar…</option>
                                <option value="Persona Física">Persona Física</option>
                                <option value="Persona Jurídica">Persona Jurídica</option>
                            </Form.Select>
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>CUIT titular / sociedad</Form.Label>
                            <Form.Control
                                name="CuitTitularOSociedad"
                                type="text"
                                maxLength={20}
                                value={form.CuitTitularOSociedad}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={12}>
                            <Form.Label className={textClass}>Razón social</Form.Label>
                            <Form.Control
                                name="RazonSocial"
                                type="text"
                                value={form.RazonSocial}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Calle</Form.Label>
                            <Form.Control
                                name="Calle"
                                type="text"
                                value={form.Calle}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={3}>
                            <Form.Label className={textClass}>Número</Form.Label>
                            <Form.Control
                                name="Numero"
                                type="text"
                                value={form.Numero}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={3}>
                            <Form.Label className={textClass}>Piso / Depto</Form.Label>
                            <Form.Control
                                name="PisoDepto"
                                type="text"
                                value={form.PisoDepto}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Localidad</Form.Label>
                            <Form.Control
                                name="Localidad"
                                type="text"
                                value={form.Localidad}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Provincia</Form.Label>
                            <Form.Select
                                name="Provincia"
                                value={form.Provincia || ""}
                                onChange={handleChange}
                                className={darkMode ? "bg-dark text-white" : ""}
                            >
                                <option value="">Seleccionar…</option>
                                {PROVINCIAS.map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Código postal</Form.Label>
                            <Form.Control
                                name="CodigoPostal"
                                type="text"
                                value={form.CodigoPostal}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Email de contacto</Form.Label>
                            <Form.Control
                                name="EmailContacto"
                                type="email"
                                value={form.EmailContacto}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Celular / WhatsApp</Form.Label>
                            <Form.Control
                                name="CelularWhatsapp"
                                type="text"
                                value={form.CelularWhatsapp}
                                onChange={handleChange}
                            />
                        </Col>

                        <Col md={6}>
                            <Form.Label className={textClass}>Asesor - Gestor</Form.Label>
                            <Form.Control
                                name="Asesor"
                                type="text"
                                value={form.Asesor}
                                onChange={handleChange}
                                placeholder="Asesor - Gestor"
                            />
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-4">
                        <Button type="submit" disabled={saving} className="border-0" style={{ background: "#B4C400" }}>
                            {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Guardando…</>) : (<><FontAwesomeIcon icon={faSave} className="me-2" /> Guardar cambios</>)}
                        </Button>
                    </div>
                </Form>

                {/* Documentos */}
                <hr className="my-4" />
                <h6 className={`lato-bold ${textClass}`}>Documentos del legajo</h6>

                {/* Subir */}
                <Form onSubmit={handleUpload} className="mt-3 mb-4">
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Label className={textClass}>Tipo de documento</Form.Label>
                            <Form.Select
                                value={tipo}
                                onChange={(e) => setTipo(e.target.value)}
                                className={darkMode ? "bg-dark text-white" : ""}
                                required
                            >
                                <option value="">Seleccionar…</option>
                                {TIPOS_DOCUMENTO.map((t) => (<option key={t} value={t}>{t}</option>))}
                            </Form.Select>
                        </Col>
                        <Col md={6}>
                            <Form.Label className={textClass}>Archivo</Form.Label>
                            <Form.Control
                                type="file"
                                accept="*/*"               // ✅ permite cualquier tipo de archivo
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                required
                            />
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-3">
                        <Button type="submit" disabled={subiendo} className="border-0" style={{ background: "#B4C400" }}>
                            {subiendo ? (<><Spinner animation="border" size="sm" className="me-2" /> Subiendo…</>) : (<><FontAwesomeIcon icon={faPlus} className="me-2" /> Agregar documento</>)}
                        </Button>
                    </div>
                </Form>

                {/* Lista */}
                <div className="table-responsive">
                    <Table hover borderless>
                        <thead>
                            <tr className="text-center">
                                <th className={`${headerClass} lato-regular fs-12 py-2`}>N°</th>
                                <th className={`${headerClass} lato-regular fs-12 py-2`}>TIPO</th>
                                <th className={`${headerClass} lato-regular fs-12 py-2`}>EXT.</th>
                                <th className={`${headerClass} lato-regular fs-12 py-2`}>TAMAÑO</th>
                                <th className={`${headerClass} lato-regular fs-12 py-2`}>ACCIONES</th>
                            </tr>
                        </thead>
                        <tbody className={`text-center ${textClass}`}>
                            {cargandoLista ? (
                                <tr><td colSpan={5}><Spinner animation="border" size="sm" /> Cargando…</td></tr>
                            ) : docList.length === 0 ? (
                                <tr><td colSpan={5} className="lato-bold fs-12-a-10">No hay documentos cargados.</td></tr>
                            ) : (
                                docList.map((d, idx) => (
                                    <tr key={d.IdDocumento ?? `${d.NombreDocumento}-${idx}`}>
                                        <td className="fs-12-a-10 lato-regular py-3">{idx + 1}</td>
                                        <td className="fs-12-a-10 lato-regular py-3">{(d.NombreDocumento || "").toUpperCase()}</td>
                                        <td className="fs-12-a-10 lato-regular py-3">{(d.DocumentoTipo || "").toUpperCase()}</td>
                                        <td className="fs-12-a-10 lato-regular py-3">{formatBytes(d.Bytes)}</td>
                                        <td className="fs-12-a-10 lato-regular py-2">
                                            <Button
                                                size="sm"
                                                className="border-0"
                                                style={{ background: "#B4C400" }}
                                                onClick={() => handleDescargar(d)}
                                                title="Descargar"
                                            >
                                                <FontAwesomeIcon icon={faDownload} /> Descargar
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ModalEditarLegajo;
